import React, { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import "../sass/pages/solicitarProjeto.scss";

const PRIORIDADES = ["BAIXA", "MEDIA", "ALTA", "CRITICA"];
const MAX_ARQUIVO_BYTES = 25 * 1024 * 1024;
const MAX_ARQUIVOS = 10;

const PRIORIDADE_META = {
  BAIXA: {
    titulo: "Baixa",
    descricao: "Pode entrar no planejamento normal.",
  },
  MEDIA: {
    titulo: "Média",
    descricao: "Importante, sem impacto imediato.",
  },
  ALTA: {
    titulo: "Alta",
    descricao: "Impacto relevante no processo.",
  },
  CRITICA: {
    titulo: "Crítica",
    descricao: "Operação parada ou risco elevado.",
  },
};

const initialForm = {
  titulo: "",
  descricaoProblema: "",
  objetivo: "",
  impacto: "",
  regraNegocio: "",
  criterioAceite: "",
  observacao: "",
  prioridadeSolicitada: "MEDIA",
  prazoDesejado: "",
};

function getUsuarioSessao() {
  const rawUser = sessionStorage.getItem("user") || localStorage.getItem("user");

  let user = {};

  try {
    user = rawUser ? JSON.parse(rawUser) : {};
  } catch {
    user = {};
  }

  const id =
    sessionStorage.getItem("IdFuncionario") ||
    sessionStorage.getItem("Idfuncionario") ||
    sessionStorage.getItem("codFunc") ||
    localStorage.getItem("IdFuncionario") ||
    localStorage.getItem("Idfuncionario") ||
    localStorage.getItem("codFunc") ||
    user?.IdFuncionario ||
    user?.idFuncionario ||
    user?.codFunc ||
    user?.id ||
    0;

  const nome =
    sessionStorage.getItem("RazaoSocial") ||
    sessionStorage.getItem("Nome") ||
    sessionStorage.getItem("nome") ||
    localStorage.getItem("RazaoSocial") ||
    localStorage.getItem("Nome") ||
    user?.nome ||
    user?.Nome ||
    user?.usuario ||
    "Usuário logado";

  return {
    id: Number(id || 0),
    nome: String(nome || "Usuário logado").trim(),
  };
}

function obterIniciais(nome) {
  const partes = String(nome || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!partes.length) return "US";

  return partes
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join("");
}

function formatarTamanho(bytes) {
  const valor = Number(bytes || 0);

  if (valor < 1024) return `${valor} B`;
  if (valor < 1024 * 1024) return `${(valor / 1024).toFixed(1)} KB`;

  return `${(valor / (1024 * 1024)).toFixed(1)} MB`;
}

function obterDataLocalHoje() {
  const hoje = new Date();
  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, "0");
  const dia = String(hoje.getDate()).padStart(2, "0");

  return `${ano}-${mes}-${dia}`;
}

function formatarDataCurta(data) {
  if (!data) return "Sem prazo definido";

  const [ano, mes, dia] = String(data).split("-");

  if (!ano || !mes || !dia) return "Sem prazo definido";

  return `${dia}/${mes}/${ano}`;
}

function obterChaveArquivo(arquivo) {
  return `${arquivo.name}-${arquivo.size}-${arquivo.lastModified}`;
}

function FieldMessage({ error, hint }) {
  if (error) {
    return (
      <span className="field-message field-message-error" role="alert">
        {error}
      </span>
    );
  }

  if (hint) {
    return <span className="field-message">{hint}</span>;
  }

  return null;
}

export default function SolicitarProjeto() {
  const usuario = useMemo(() => getUsuarioSessao(), []);
  const iniciaisUsuario = useMemo(
    () => obterIniciais(usuario.nome),
    [usuario.nome]
  );
  const hoje = useMemo(() => obterDataLocalHoje(), []);
  const draftKey = useMemo(
    () => `solicitar-projeto:rascunho:${usuario.id || "anonimo"}`,
    [usuario.id]
  );

  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const draftCarregadoRef = useRef(false);

  const [salvando, setSalvando] = useState(false);
  const [statusEnvio, setStatusEnvio] = useState("");
  const [arquivos, setArquivos] = useState([]);
  const [dragAtivo, setDragAtivo] = useState(false);
  const [erros, setErros] = useState({});
  const [rascunhoSalvoEm, setRascunhoSalvoEm] = useState(null);
  const [ultimaSolicitacao, setUltimaSolicitacao] = useState(null);

  const [form, setForm] = useState(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      const draft = raw ? JSON.parse(raw) : null;

      return draft && typeof draft === "object"
        ? { ...initialForm, ...draft }
        : initialForm;
    } catch {
      return initialForm;
    }
  });

  const temConteudo = useMemo(
    () =>
      Object.entries(form).some(([campo, valor]) => {
        if (campo === "prioridadeSolicitada") return valor !== "MEDIA";
        return String(valor || "").trim().length > 0;
      }) || arquivos.length > 0,
    [form, arquivos.length]
  );

  const tamanhoTotalArquivos = useMemo(
    () => arquivos.reduce((total, arquivo) => total + arquivo.size, 0),
    [arquivos]
  );

  const progresso = useMemo(() => {
    const itens = [
      Boolean(form.titulo.trim()),
      Boolean(form.descricaoProblema.trim()),
      Boolean(form.criterioAceite.trim()),
      Boolean(form.objetivo.trim()),
      Boolean(form.impacto.trim()),
      Boolean(form.regraNegocio.trim()),
      Boolean(form.prazoDesejado),
      arquivos.length > 0,
    ];

    const preenchidos = itens.filter(Boolean).length;

    return {
      percentual: Math.round((preenchidos / itens.length) * 100),
      preenchidos,
      total: itens.length,
    };
  }, [form, arquivos.length]);

  const checklist = useMemo(
    () => [
      {
        label: "Título informado",
        concluido: Boolean(form.titulo.trim()),
        obrigatorio: true,
      },
      {
        label: "Problema descrito",
        concluido: Boolean(form.descricaoProblema.trim()),
        obrigatorio: true,
      },
      {
        label: "Critério de aceite definido",
        concluido: Boolean(form.criterioAceite.trim()),
        obrigatorio: true,
      },
      {
        label: "Objetivo e impacto contextualizados",
        concluido: Boolean(form.objetivo.trim() && form.impacto.trim()),
      },
      {
        label: "Regras do processo documentadas",
        concluido: Boolean(form.regraNegocio.trim()),
      },
      {
        label: "Evidências anexadas",
        concluido: arquivos.length > 0,
      },
    ],
    [form, arquivos.length]
  );

  useEffect(() => {
    draftCarregadoRef.current = true;
  }, []);

  useEffect(() => {
    if (!draftCarregadoRef.current || salvando) return undefined;

    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(draftKey, JSON.stringify(form));
        setRascunhoSalvoEm(new Date());
      } catch {
        // O formulário continua funcionando mesmo se o navegador bloquear storage.
      }
    }, 600);

    return () => window.clearTimeout(timer);
  }, [draftKey, form, salvando]);

  function limparErro(campo) {
    setErros((prev) => {
      if (!prev[campo]) return prev;

      const novo = { ...prev };
      delete novo[campo];
      return novo;
    });
  }

  function alterarCampo(campo, valor) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
    limparErro(campo);
    setUltimaSolicitacao(null);
  }

  function limparFormulario({ confirmar = true } = {}) {
    if (
      confirmar &&
      temConteudo &&
      !window.confirm("Deseja limpar todo o conteúdo desta solicitação?")
    ) {
      return;
    }

    setForm(initialForm);
    setArquivos([]);
    setErros({});
    setDragAtivo(false);
    setRascunhoSalvoEm(null);

    try {
      localStorage.removeItem(draftKey);
    } catch {
      // Ignora falha de storage.
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  function focarPrimeiroErro(novosErros) {
    const primeiroCampo = Object.keys(novosErros)[0];

    if (!primeiroCampo) return;

    window.setTimeout(() => {
      const elemento = formRef.current?.querySelector(
        `[data-field="${primeiroCampo}"]`
      );

      elemento?.focus();
    }, 0);
  }

  function validar() {
    const novosErros = {};

    if (!usuario.id) {
      showError("Sessão sem IdFuncionario. Faça login novamente.");
      return false;
    }

    if (!form.titulo.trim()) {
      novosErros.titulo = "Informe um título claro para a solicitação.";
    } else if (form.titulo.trim().length < 5) {
      novosErros.titulo = "Use pelo menos 5 caracteres no título.";
    }

    if (!form.descricaoProblema.trim()) {
      novosErros.descricaoProblema =
        "Descreva o problema que precisa ser resolvido.";
    } else if (form.descricaoProblema.trim().length < 20) {
      novosErros.descricaoProblema =
        "Detalhe um pouco mais o problema, com pelo menos 20 caracteres.";
    }

    if (!form.criterioAceite.trim()) {
      novosErros.criterioAceite =
        "Informe como a equipe poderá validar que a entrega ficou correta.";
    } else if (form.criterioAceite.trim().length < 10) {
      novosErros.criterioAceite =
        "O critério de aceite precisa ser mais objetivo.";
    }

    if (form.prazoDesejado && form.prazoDesejado < hoje) {
      novosErros.prazoDesejado = "O prazo não pode ser anterior à data atual.";
    }

    setErros(novosErros);

    if (Object.keys(novosErros).length) {
      showError("Revise os campos destacados antes de enviar.");
      focarPrimeiroErro(novosErros);
      return false;
    }

    return true;
  }

  function adicionarArquivos(novosArquivos) {
    const recebidos = Array.from(novosArquivos || []);

    if (!recebidos.length) return;

    const muitoGrandes = recebidos.filter(
      (arquivo) => arquivo.size > MAX_ARQUIVO_BYTES
    );

    if (muitoGrandes.length) {
      showError(
        `Cada anexo pode ter no máximo 25 MB. Removidos: ${muitoGrandes
          .map((arquivo) => arquivo.name)
          .join(", ")}`
      );
    }

    const permitidos = recebidos.filter(
      (arquivo) => arquivo.size <= MAX_ARQUIVO_BYTES
    );

    setArquivos((atuais) => {
      const chavesExistentes = new Set(atuais.map(obterChaveArquivo));
      const semDuplicados = permitidos.filter(
        (arquivo) => !chavesExistentes.has(obterChaveArquivo(arquivo))
      );

      const combinados = [...atuais, ...semDuplicados];

      if (combinados.length > MAX_ARQUIVOS) {
        showError(`É possível anexar até ${MAX_ARQUIVOS} arquivos.`);
      }

      return combinados.slice(0, MAX_ARQUIVOS);
    });

    setUltimaSolicitacao(null);
  }

  function removerArquivo(indice) {
    setArquivos((atuais) => atuais.filter((_, index) => index !== indice));
  }

  function abrirSeletorArquivos() {
    if (!salvando) {
      fileInputRef.current?.click();
    }
  }

  function tratarDrop(event) {
    event.preventDefault();
    setDragAtivo(false);

    if (salvando) return;

    adicionarArquivos(event.dataTransfer.files);
  }

  async function enviarArquivos(idSolicitacao) {
    if (!arquivos.length) {
      return {
        enviados: 0,
        falhas: [],
      };
    }

    const falhas = [];
    let enviados = 0;

    for (let index = 0; index < arquivos.length; index += 1) {
      const arquivo = arquivos[index];

      setStatusEnvio(
        `Enviando anexo ${index + 1} de ${arquivos.length}: ${arquivo.name}`
      );

      const formData = new FormData();
      formData.append("file", arquivo);

      try {
        await api.post(
          `/ssw/projeto-solicitacoes/${idSolicitacao}/anexos`,
          formData
        );
        enviados += 1;
      } catch (error) {
        console.error(
          `[SolicitarProjeto.enviarArquivos][${arquivo.name}][ERR]`,
          error
        );

        falhas.push({
          arquivo: arquivo.name,
          mensagem:
            error?.response?.data?.message || "Falha ao enviar o arquivo.",
        });
      }
    }

    return {
      enviados,
      falhas,
    };
  }

  async function salvar(event) {
    event.preventDefault();

    if (!validar()) return;

    setSalvando(true);
    setStatusEnvio("Registrando solicitação...");

    try {
      const payload = {
        titulo: form.titulo.trim(),
        descricaoProblema: form.descricaoProblema.trim(),
        objetivo: form.objetivo?.trim() || null,
        impacto: form.impacto?.trim() || null,
        regraNegocio: form.regraNegocio?.trim() || null,
        criterioAceite: form.criterioAceite.trim(),
        observacao: form.observacao?.trim() || null,
        prioridadeSolicitada: form.prioridadeSolicitada,
        prazoDesejado: form.prazoDesejado || null,
        solicitanteId: usuario.id,
      };

      const { data } = await api.post("/ssw/projeto-solicitacoes", payload);
      const idSolicitacao = Number(data?.ID_SOLICITACAO || 0);

      const resultadoAnexos = idSolicitacao
        ? await enviarArquivos(idSolicitacao)
        : { enviados: 0, falhas: [] };

      setUltimaSolicitacao({
        id: idSolicitacao || null,
        anexosEnviados: resultadoAnexos.enviados,
        anexosComFalha: resultadoAnexos.falhas.length,
      });

      showInfo(
        idSolicitacao
          ? `Solicitação #${idSolicitacao} registrada com sucesso.`
          : "Solicitação registrada com sucesso."
      );

      if (resultadoAnexos.falhas.length) {
        showError(
          `A solicitação foi criada, mas ${resultadoAnexos.falhas.length} anexo(s) não foram enviados: ${resultadoAnexos.falhas
            .map((item) => item.arquivo)
            .join(", ")}`
        );
      }

      limparFormulario({ confirmar: false });
    } catch (error) {
      console.error("[SolicitarProjeto.salvar][ERR]", error);

      const detalhes = error?.response?.data?.details;
      const mensagemDetalhes = Array.isArray(detalhes)
        ? detalhes
            .map((item) => item?.message)
            .filter(Boolean)
            .join(" | ")
        : "";

      showError(
        mensagemDetalhes ||
          error?.response?.data?.message ||
          "Falha ao registrar solicitação."
      );
    } finally {
      setSalvando(false);
      setStatusEnvio("");
    }
  }

  return (
    <MainLayout>
      <div className="solicitar-projeto-page">
        <div className="solicitar-shell">
          <header className="solicitar-header">
            <div className="header-copy">
              <span className="page-eyebrow">Nova demanda</span>
              <h1>Solicitar projeto</h1>
              <p>
                Organize o contexto da necessidade para que a gestão consiga
                analisar, priorizar e encaminhar a demanda com mais precisão.
              </p>
            </div>

            <div className="solicitante-card">
              <div className="solicitante-avatar" aria-hidden="true">
                {iniciaisUsuario}
              </div>

              <div className="solicitante-info">
                <span>Solicitante</span>
                <strong>{usuario.nome}</strong>
                <small>
                  {usuario.id
                    ? `Funcionário #${usuario.id}`
                    : "Sessão sem identificação"}
                </small>
              </div>

              <span
                className={`session-status ${
                  usuario.id ? "is-online" : "is-offline"
                }`}
                title={
                  usuario.id
                    ? "Sessão identificada"
                    : "Faça login novamente antes de enviar"
                }
              />
            </div>
          </header>

          {ultimaSolicitacao && (
            <div className="success-banner" role="status">
              <div className="success-icon" aria-hidden="true">
                ✓
              </div>

              <div>
                <strong>
                  Solicitação
                  {ultimaSolicitacao.id
                    ? ` #${ultimaSolicitacao.id}`
                    : ""}{" "}
                  registrada
                </strong>
                <p>
                  {ultimaSolicitacao.anexosEnviados > 0
                    ? `${ultimaSolicitacao.anexosEnviados} anexo(s) enviado(s). `
                    : ""}
                  {ultimaSolicitacao.anexosComFalha > 0
                    ? `${ultimaSolicitacao.anexosComFalha} anexo(s) precisam ser reenviados.`
                    : "A demanda já está disponível para análise da gestão."}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setUltimaSolicitacao(null)}
                aria-label="Fechar mensagem"
              >
                ×
              </button>
            </div>
          )}

          <div className="solicitar-layout">
            <main className="solicitar-main">
              <form
                ref={formRef}
                className="solicitar-card"
                onSubmit={salvar}
                noValidate
              >
                <section className="form-section">
                  <div className="section-heading">
                    <span className="section-number">01</span>
                    <div>
                      <h2>Identificação da demanda</h2>
                      <p>
                        Dê um nome objetivo e explique claramente o problema.
                      </p>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div
                      className={`field field-full ${
                        erros.titulo ? "has-error" : ""
                      }`}
                    >
                      <div className="field-label-row">
                        <label htmlFor="titulo">
                          Título da solicitação
                          <span className="required-mark">*</span>
                        </label>
                        <span className="char-counter">
                          {form.titulo.length}/120
                        </span>
                      </div>

                      <input
                        id="titulo"
                        data-field="titulo"
                        value={form.titulo}
                        onChange={(event) =>
                          alterarCampo("titulo", event.target.value)
                        }
                        placeholder="Ex.: Automatizar conferência fiscal das notas recebidas"
                        maxLength={120}
                        disabled={salvando}
                        aria-invalid={Boolean(erros.titulo)}
                      />

                      <FieldMessage
                        error={erros.titulo}
                        hint="Use uma frase curta que identifique claramente a entrega."
                      />
                    </div>

                    <div
                      className={`field field-full ${
                        erros.descricaoProblema ? "has-error" : ""
                      }`}
                    >
                      <div className="field-label-row">
                        <label htmlFor="descricaoProblema">
                          Problema que precisa ser resolvido
                          <span className="required-mark">*</span>
                        </label>
                        <span className="char-counter">
                          {form.descricaoProblema.length}/4000
                        </span>
                      </div>

                      <textarea
                        id="descricaoProblema"
                        data-field="descricaoProblema"
                        value={form.descricaoProblema}
                        onChange={(event) =>
                          alterarCampo(
                            "descricaoProblema",
                            event.target.value
                          )
                        }
                        placeholder="Explique o cenário atual, onde o problema acontece, com que frequência e quais dificuldades ele causa."
                        rows={5}
                        maxLength={4000}
                        disabled={salvando}
                        aria-invalid={Boolean(erros.descricaoProblema)}
                      />

                      <FieldMessage
                        error={erros.descricaoProblema}
                        hint="Evite descrever apenas a solução. Primeiro deixe claro o problema atual."
                      />
                    </div>
                  </div>
                </section>

                <section className="form-section">
                  <div className="section-heading">
                    <span className="section-number">02</span>
                    <div>
                      <h2>Contexto e resultado esperado</h2>
                      <p>
                        Ajude a equipe a compreender o valor e o alcance da
                        mudança.
                      </p>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="field">
                      <div className="field-label-row">
                        <label htmlFor="objetivo">Objetivo</label>
                        <span className="char-counter">
                          {form.objetivo.length}/4000
                        </span>
                      </div>

                      <textarea
                        id="objetivo"
                        data-field="objetivo"
                        value={form.objetivo}
                        onChange={(event) =>
                          alterarCampo("objetivo", event.target.value)
                        }
                        placeholder="Qual resultado deve ser alcançado após a entrega?"
                        rows={4}
                        maxLength={4000}
                        disabled={salvando}
                      />

                      <FieldMessage hint="Descreva o resultado, não a implementação técnica." />
                    </div>

                    <div className="field">
                      <div className="field-label-row">
                        <label htmlFor="impacto">Impacto</label>
                        <span className="char-counter">
                          {form.impacto.length}/4000
                        </span>
                      </div>

                      <textarea
                        id="impacto"
                        data-field="impacto"
                        value={form.impacto}
                        onChange={(event) =>
                          alterarCampo("impacto", event.target.value)
                        }
                        placeholder="Quem é afetado e o que acontece enquanto a demanda não é atendida?"
                        rows={4}
                        maxLength={4000}
                        disabled={salvando}
                      />

                      <FieldMessage hint="Considere clientes, suporte, operação, fiscal e financeiro." />
                    </div>
                  </div>
                </section>

                <section className="form-section">
                  <div className="section-heading">
                    <span className="section-number">03</span>
                    <div>
                      <h2>Regras e validação da entrega</h2>
                      <p>
                        Registre as condições do processo e como o resultado será
                        homologado.
                      </p>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="field field-full">
                      <div className="field-label-row">
                        <label htmlFor="regraNegocio">Regra de negócio</label>
                        <span className="char-counter">
                          {form.regraNegocio.length}/4000
                        </span>
                      </div>

                      <textarea
                        id="regraNegocio"
                        data-field="regraNegocio"
                        value={form.regraNegocio}
                        onChange={(event) =>
                          alterarCampo("regraNegocio", event.target.value)
                        }
                        placeholder="Informe regras, exceções, permissões, cálculos, validações e comportamentos obrigatórios."
                        rows={5}
                        maxLength={4000}
                        disabled={salvando}
                      />

                      <FieldMessage hint="Liste cenários especiais que não podem ser esquecidos durante o desenvolvimento." />
                    </div>

                    <div
                      className={`field field-full ${
                        erros.criterioAceite ? "has-error" : ""
                      }`}
                    >
                      <div className="field-label-row">
                        <label htmlFor="criterioAceite">
                          Critério de aceite
                          <span className="required-mark">*</span>
                        </label>
                        <span className="char-counter">
                          {form.criterioAceite.length}/4000
                        </span>
                      </div>

                      <textarea
                        id="criterioAceite"
                        data-field="criterioAceite"
                        value={form.criterioAceite}
                        onChange={(event) =>
                          alterarCampo("criterioAceite", event.target.value)
                        }
                        placeholder="Ex.: Ao importar uma NF-e válida, o sistema deve criar os produtos, exibir os campos fiscais e impedir duplicidade de código."
                        rows={5}
                        maxLength={4000}
                        disabled={salvando}
                        aria-invalid={Boolean(erros.criterioAceite)}
                      />

                      <FieldMessage
                        error={erros.criterioAceite}
                        hint="Prefira critérios objetivos, verificáveis e com resultado esperado."
                      />
                    </div>
                  </div>
                </section>

                <section className="form-section">
                  <div className="section-heading">
                    <span className="section-number">04</span>
                    <div>
                      <h2>Planejamento sugerido</h2>
                      <p>
                        A prioridade final será definida pela gestão, mas seu
                        contexto ajuda na análise.
                      </p>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="field field-full">
                      <label>Prioridade sugerida</label>

                      <div className="priority-grid" role="radiogroup">
                        {PRIORIDADES.map((prioridade) => {
                          const meta = PRIORIDADE_META[prioridade];
                          const selecionada =
                            form.prioridadeSolicitada === prioridade;

                          return (
                            <label
                              key={prioridade}
                              className={`priority-option priority-${prioridade.toLowerCase()} ${
                                selecionada ? "is-selected" : ""
                              }`}
                            >
                              <input
                                type="radio"
                                name="prioridadeSolicitada"
                                value={prioridade}
                                checked={selecionada}
                                disabled={salvando}
                                onChange={(event) =>
                                  alterarCampo(
                                    "prioridadeSolicitada",
                                    event.target.value
                                  )
                                }
                              />

                              <span className="priority-indicator" />

                              <span className="priority-copy">
                                <strong>{meta.titulo}</strong>
                                <small>{meta.descricao}</small>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div
                      className={`field ${
                        erros.prazoDesejado ? "has-error" : ""
                      }`}
                    >
                      <label htmlFor="prazoDesejado">Prazo desejado</label>

                      <input
                        id="prazoDesejado"
                        data-field="prazoDesejado"
                        type="date"
                        min={hoje}
                        value={form.prazoDesejado}
                        onChange={(event) =>
                          alterarCampo("prazoDesejado", event.target.value)
                        }
                        disabled={salvando}
                        aria-invalid={Boolean(erros.prazoDesejado)}
                      />

                      <FieldMessage
                        error={erros.prazoDesejado}
                        hint="Opcional. A gestão poderá revisar a data durante a priorização."
                      />
                    </div>

                    <div className="field">
                      <div className="field-label-row">
                        <label htmlFor="observacao">
                          Observações adicionais
                        </label>
                        <span className="char-counter">
                          {form.observacao.length}/4000
                        </span>
                      </div>

                      <textarea
                        id="observacao"
                        data-field="observacao"
                        value={form.observacao}
                        onChange={(event) =>
                          alterarCampo("observacao", event.target.value)
                        }
                        placeholder="Cliente de referência, ambiente afetado, cenário de teste ou informação interna."
                        rows={4}
                        maxLength={4000}
                        disabled={salvando}
                      />
                    </div>
                  </div>
                </section>

                <section className="form-section">
                  <div className="section-heading">
                    <span className="section-number">05</span>
                    <div>
                      <h2>Evidências e anexos</h2>
                      <p>
                        Inclua prints, documentos ou arquivos que facilitem o
                        entendimento da demanda.
                      </p>
                    </div>
                  </div>

                  <div className="form-grid">
                    <div className="field field-full">
                      <input
                        ref={fileInputRef}
                        className="file-input-hidden"
                        type="file"
                        multiple
                        disabled={salvando}
                        onChange={(event) => {
                          adicionarArquivos(event.target.files);
                          event.target.value = "";
                        }}
                      />

                      <div
                        className={`upload-zone ${
                          dragAtivo ? "is-dragging" : ""
                        } ${salvando ? "is-disabled" : ""}`}
                        role="button"
                        tabIndex={salvando ? -1 : 0}
                        onClick={abrirSeletorArquivos}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            abrirSeletorArquivos();
                          }
                        }}
                        onDragEnter={(event) => {
                          event.preventDefault();
                          if (!salvando) setDragAtivo(true);
                        }}
                        onDragOver={(event) => event.preventDefault()}
                        onDragLeave={(event) => {
                          event.preventDefault();

                          if (
                            !event.currentTarget.contains(event.relatedTarget)
                          ) {
                            setDragAtivo(false);
                          }
                        }}
                        onDrop={tratarDrop}
                      >
                        <div className="upload-icon" aria-hidden="true">
                          ↑
                        </div>

                        <div className="upload-copy">
                          <strong>
                            Arraste os arquivos para cá ou clique para selecionar
                          </strong>
                          <span>
                            Até {MAX_ARQUIVOS} arquivos, com no máximo 25 MB por
                            anexo
                          </span>
                        </div>

                        <span className="upload-action">Selecionar arquivos</span>
                      </div>

                      {arquivos.length > 0 && (
                        <div className="arquivos-panel">
                          <div className="arquivos-panel-header">
                            <div>
                              <strong>
                                {arquivos.length} arquivo(s) selecionado(s)
                              </strong>
                              <span>
                                Tamanho total:{" "}
                                {formatarTamanho(tamanhoTotalArquivos)}
                              </span>
                            </div>

                            <button
                              type="button"
                              disabled={salvando}
                              onClick={() => setArquivos([])}
                            >
                              Remover todos
                            </button>
                          </div>

                          <div className="arquivos-lista">
                            {arquivos.map((arquivo, index) => (
                              <div
                                className="arquivo-item"
                                key={obterChaveArquivo(arquivo)}
                              >
                                <div className="arquivo-icone" aria-hidden="true">
                                  {String(arquivo.name)
                                    .split(".")
                                    .pop()
                                    ?.slice(0, 4)
                                    .toUpperCase() || "DOC"}
                                </div>

                                <div className="arquivo-info">
                                  <strong title={arquivo.name}>
                                    {arquivo.name}
                                  </strong>
                                  <span>{formatarTamanho(arquivo.size)}</span>
                                </div>

                                <button
                                  type="button"
                                  disabled={salvando}
                                  onClick={() => removerArquivo(index)}
                                  aria-label={`Remover ${arquivo.name}`}
                                  title="Remover arquivo"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                <footer className="form-actions">
                  <div className="action-status">
                    <span
                      className={`draft-dot ${
                        rascunhoSalvoEm ? "is-saved" : ""
                      }`}
                    />

                    <span>
                      {salvando
                        ? statusEnvio || "Processando solicitação..."
                        : rascunhoSalvoEm
                        ? `Rascunho salvo às ${rascunhoSalvoEm.toLocaleTimeString(
                            "pt-BR",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}`
                        : "O preenchimento será salvo automaticamente neste navegador."}
                    </span>
                  </div>

                  <div className="action-buttons">
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={salvando || !temConteudo}
                      onClick={() => limparFormulario()}
                    >
                      Limpar formulário
                    </button>

                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={salvando || !usuario.id}
                    >
                      {salvando ? (
                        <>
                          <span className="button-spinner" aria-hidden="true" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <span aria-hidden="true">→</span>
                          Enviar para análise
                        </>
                      )}
                    </button>
                  </div>
                </footer>
              </form>
            </main>

            <aside className="solicitar-sidebar">
              <div className="sidebar-card progress-card">
                <div className="sidebar-card-header">
                  <div>
                    <span>Qualidade do escopo</span>
                    <strong>{progresso.percentual}%</strong>
                  </div>

                  <div
                    className="progress-ring"
                    style={{
                      "--progress": `${progresso.percentual * 3.6}deg`,
                    }}
                    aria-label={`${progresso.percentual}% preenchido`}
                  >
                    <span>{progresso.preenchidos}</span>
                  </div>
                </div>

                <div className="progress-track">
                  <span style={{ width: `${progresso.percentual}%` }} />
                </div>

                <p>
                  Quanto mais contexto for informado, mais rápida e precisa será
                  a análise da gestão.
                </p>
              </div>

              <div className="sidebar-card checklist-card">
                <div className="sidebar-title">
                  <span>Revisão antes do envio</span>
                  <small>
                    {checklist.filter((item) => item.concluido).length}/
                    {checklist.length}
                  </small>
                </div>

                <ul>
                  {checklist.map((item) => (
                    <li
                      key={item.label}
                      className={item.concluido ? "is-complete" : ""}
                    >
                      <span className="check-icon" aria-hidden="true">
                        {item.concluido ? "✓" : ""}
                      </span>

                      <span>
                        {item.label}
                        {item.obrigatorio && <small>Obrigatório</small>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="sidebar-card summary-card">
                <div className="sidebar-title">
                  <span>Resumo da solicitação</span>
                </div>

                <dl>
                  <div>
                    <dt>Prioridade</dt>
                    <dd
                      className={`summary-priority priority-${form.prioridadeSolicitada.toLowerCase()}`}
                    >
                      {PRIORIDADE_META[form.prioridadeSolicitada].titulo}
                    </dd>
                  </div>

                  <div>
                    <dt>Prazo desejado</dt>
                    <dd>{formatarDataCurta(form.prazoDesejado)}</dd>
                  </div>

                  <div>
                    <dt>Anexos</dt>
                    <dd>
                      {arquivos.length
                        ? `${arquivos.length} arquivo(s)`
                        : "Nenhum"}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="sidebar-tip">
                <span className="tip-icon" aria-hidden="true">
                  i
                </span>

                <p>
                  <strong>Dica:</strong> descreva o comportamento esperado como
                  se outra pessoa fosse validar a entrega sem conhecer o
                  contexto anterior.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
